"use client";

import {
	compareSdkVersions,
	type EmbedSearch,
	type EmbedTransport,
	PRELOAD_SINCE,
} from "@repo/embed";
import { useEffect, useEffectEvent, useRef, useState } from "react";

/**
 * Whether the chat is on screen (SDK-PRELOAD.md 3.4). A native SDK loads the page ahead of the
 * tap and keeps it after the sheet closes, and Page Visibility cannot tell: a web view that was
 * never attached counts as visible on Android, one outside any window is hidden to WebKit, and
 * a React Native view moved off screen stays visible on both. So the SDK says (`visibility`).
 */

const NATIVE_EMBEDS = [
	"flutter",
	"react-native",
	"ios",
	"android",
] as const satisfies readonly EmbedTransport[];
export type NativeEmbed = (typeof NATIVE_EMBEDS)[number];

/** The embed when a native SDK's web view holds the page; null in a browser, iframe or widget. */
export function nativeEmbed(
	embed: EmbedTransport | undefined,
): NativeEmbed | null {
	return NATIVE_EMBEDS.find((native) => native === embed) ?? null;
}

/**
 * Only a page whose SDK will tell it when it is shown starts hidden. Everything else (older
 * SDKs, no `sdk`, iframes, the widget, a browser) is on screen from the start. `sdk` is the
 * route's search, which `embedSearchSchema` already holds to `X.Y.Z`.
 */
export function startsHidden({
	embed,
	sdk,
}: Pick<EmbedSearch, "embed" | "sdk">): boolean {
	return (
		nativeEmbed(embed) !== null &&
		sdk !== undefined &&
		compareSdkVersions(sdk, PRELOAD_SINCE) >= 0
	);
}

/**
 * - idle: nothing is being answered
 * - busy: a reply is on its way
 * - error: the last turn failed
 */
export type TurnState = "idle" | "busy" | "error";

export function useShown({
	startedHidden,
	turn,
	settled,
	onHide,
	onShow,
	reload,
	clearError,
}: {
	/** `startsHidden` of the page's search. */
	startedHidden: boolean;
	turn: TurnState;
	/** The page finished restoring its session and conversation. */
	settled: boolean;
	/** Left the screen: end what must not go on unseen (a call). */
	onHide: () => void;
	/** Back on screen after being hidden, whatever the turn is doing. */
	onShow: () => void;
	/** Reads the conversation again from the server; also the session check. */
	reload: () => void;
	clearError: () => void;
}) {
	const [shown, setShown] = useState(!startedHidden);
	// What handlers read: a host's `focus` can follow its `visibility` before the next render.
	const current = useRef(shown);
	// A reply still streaming when the chat came back: it is left alone (the server ties the
	// turn to its request, and a reload mid-stream would mark its tool calls as announced).
	const streamingAtShow = useRef(false);

	const hidden = () => {
		streamingAtShow.current = false;
		onHide();
		// The keyboard goes with the chat where the web view stays in the window.
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}
	};

	const cameBack = () => {
		onShow();
		if (!settled) {
			// Still restoring: that load is the fresh one.
			return;
		}
		if (turn === "busy") {
			streamingAtShow.current = true;
			return;
		}
		if (turn === "error") {
			// It failed unseen (a suspended web view drops its stream): no banner greets
			// the shopper, the server has what was saved.
			clearError();
		}
		reload();
	};

	/** On or off screen, from the host's `visibility` or a touch; a repeat does nothing. */
	const set = (next: boolean) => {
		if (current.current === next) {
			return;
		}
		current.current = next;
		setShown(next);
		if (next) {
			cameBack();
		} else {
			hidden();
		}
	};

	// Fail open: a shopper who can touch the chat is looking at it, whatever became of the
	// host's message.
	const touched = useEffectEvent((event: Event) => {
		if (event.isTrusted) {
			set(true);
		}
	});
	useEffect(() => {
		if (shown) {
			return;
		}
		const open = (event: Event) => touched(event);
		document.addEventListener("pointerdown", open, true);
		document.addEventListener("keydown", open, true);
		return () => {
			document.removeEventListener("pointerdown", open, true);
			document.removeEventListener("keydown", open, true);
		};
	}, [shown]);

	const turnChanged = useEffectEvent(() => {
		if (!streamingAtShow.current || turn === "busy") {
			return;
		}
		streamingAtShow.current = false;
		if (turn === "error") {
			clearError();
			reload();
		}
	});
	// biome-ignore lint/correctness/useExhaustiveDependencies: runs when the turn's state changes
	useEffect(() => turnChanged(), [turn]);

	return {
		shown,
		/** The value now, for handlers that run before the next render. */
		isShown: () => current.current,
		set,
	};
}
